export enum StoryCategory {
  Industry,
  Size,
  UseCase,
}

export enum IndustrySubCategory {
  Technology = 0,
  Automotive,
  Financial,
}

export enum SizeSubCategory {
  Enterprises = 3,
  Teams,
  Startups,
}

export enum UseCaseSubCategory {
  DevSecOps = 6,
  Automation,
  DevOps,
}

export type StorySubCategory = IndustrySubCategory | SizeSubCategory | UseCaseSubCategory

interface CustomerStory {
  category: StoryCategory
  subCategory: StorySubCategory
  description: string
  url: string
  backgroundUrl: string
  logo: {
    url: string
    alt: string
    height?: string
  }
}

type CustomerStoryData = Omit<CustomerStory, 'category' | 'subCategory' | 'logo' | 'url' | 'backgroundUrl'> & {
  logo: Omit<CustomerStory['logo'], 'url'>
}

type SubCategoryDictionaryData = Record<string, CustomerStoryData>
type SubCategoryDictionary = Record<string, CustomerStory>
type CustomerStoryDictionary = Record<StoryCategory, Partial<Record<StorySubCategory, SubCategoryDictionary>>>
type CustomerStoryDataDictionary = Record<StoryCategory, Partial<Record<StorySubCategory, SubCategoryDictionaryData>>>

const technologyStories: SubCategoryDictionaryData = {
  figma: {
    description: 'Figma streamlines development and strengthens security',
    logo: {
      alt: 'Figma’s logo',
      height: '35px',
    },
  },
}

const automotiveStories: SubCategoryDictionaryData = {
  mercedesBenz: {
    description: 'Mercedez-Benz standardizes source code and automates onboarding',
    logo: {
      alt: 'Mercedes-Benz’s logo',
      height: '48px',
    },
  },
}

const financialStories: SubCategoryDictionaryData = {
  mercadoLibre: {
    description: 'Mercado Libre cuts coding time by 50%',
    logo: {
      alt: 'Mercado Libre’s logo',
      height: '40px',
    },
  },
}

const enterprisesStories: SubCategoryDictionaryData = {
  accenture: {
    description: 'Accenture customizes GitHub Copilot for its 12,000 developers',
    logo: {
      alt: 'Accenture’s logo',
      height: '36px',
    },
  },
  philips: {
    description: 'Philips reduces infrastructure costs by 80%',
    logo: {
      alt: 'Philips’ logo',
      height: '34px',
    },
  },
}

const teamsStories: SubCategoryDictionaryData = {
  buffer: {
    description: 'Buffer enhances collaboration and control with GitHub Team',
    logo: {
      alt: 'Buffer’s logo',
      height: '32px',
    },
  },
}

const devSecOpsStories: SubCategoryDictionaryData = {
  telus: {
    description: 'TELUS saves $16.9M with GitHub',
    logo: {
      alt: 'Telus’ logo',
      height: '36px',
    },
  },
}

const automationStories: SubCategoryDictionaryData = {
  fullstory: {
    description: 'Fullstory automates DevSecOps at scale with GitHub',
    logo: {
      alt: 'Fullstory’s logo',
      height: '36px',
    },
  },
}

const devOpsStories: SubCategoryDictionaryData = {
  ey: {
    description: 'EY leverages GitHub and Microsoft Azure DevOps to outpace the competition',
    logo: {
      alt: 'EY’s logo',
      height: '52px',
    },
  },
}

const customerStoriesData: CustomerStoryDataDictionary = {
  [StoryCategory.Industry]: {
    [IndustrySubCategory.Technology]: technologyStories,
    [IndustrySubCategory.Automotive]: automotiveStories,
    [IndustrySubCategory.Financial]: financialStories,
  },
  [StoryCategory.Size]: {
    [SizeSubCategory.Enterprises]: enterprisesStories,
    [SizeSubCategory.Teams]: teamsStories,
  },
  [StoryCategory.UseCase]: {
    [UseCaseSubCategory.DevSecOps]: devSecOpsStories,
    [UseCaseSubCategory.Automation]: automationStories,
    [UseCaseSubCategory.DevOps]: devOpsStories,
  },
}

const LOGO_PATH = '/images/modules/site/home24/customer-stories/logos'
const BACKGROUND_PATH = '/images/modules/site/home24/customer-stories/backgrounds'
const BASE_URL = 'https://github.com/customer-stories'

const toKebabCase = (string: string) => {
  return string
    .replace(/([a-z])([A-Z])/g, '$1-$2') // Insert dash between lowercase and uppercase letters
    .replace(/([A-Z])([A-Z][a-z])/g, '$1-$2') // Handle cases like 'XMLHttpRequest'
    .toLowerCase() // Convert all to lowercase
}

export const CUSTOMER_STORIES = {} as CustomerStoryDictionary

// eslint-disable-next-line github/array-foreach
Object.entries(customerStoriesData).forEach(([category, subCategories]) => {
  const typedCategory = Number(category) as StoryCategory
  if (!CUSTOMER_STORIES[typedCategory]) CUSTOMER_STORIES[typedCategory] = {}

  // eslint-disable-next-line github/array-foreach
  Object.entries(subCategories).forEach(([subCategory, stories]) => {
    const typedSubCategory = Number(subCategory) as StorySubCategory

    if (!CUSTOMER_STORIES[typedCategory][typedSubCategory]) CUSTOMER_STORIES[typedCategory][typedSubCategory] = {}

    // eslint-disable-next-line github/array-foreach
    Object.entries(stories).forEach(([customerId, story]) => {
      const parsedCustomerId = toKebabCase(customerId)

      // @ts-expect-error The object is correctly initialized above
      CUSTOMER_STORIES[typedCategory][typedSubCategory][customerId] = {
        ...story,
        category: typedCategory,
        subCategory: typedSubCategory,
        url: `${BASE_URL}/${parsedCustomerId}`,
        backgroundUrl: `${BACKGROUND_PATH}/${parsedCustomerId}.webp`,
        logo: {
          ...story.logo,
          url: `${LOGO_PATH}/${parsedCustomerId}.svg`,
        },
      }
    })
  })
})

export const COPY = {
  analyticsId: 'customer_stories',
  intro: {
    title:
      'From startups to enterprises, <span>GitHub scales <br class="lp-breakText" /> with teams of any size in any industry.</span>',
  },
  content: {
    categories: {
      [StoryCategory.Industry]: 'By industry',
      [StoryCategory.Size]: 'By size',
      [StoryCategory.UseCase]: 'By use case',
    },
    subCategories: {
      [IndustrySubCategory.Technology]: 'Technology',
      [IndustrySubCategory.Automotive]: 'Automotive',
      [IndustrySubCategory.Financial]: 'Financial services',
      [SizeSubCategory.Enterprises]: 'Enterprises',
      [SizeSubCategory.Teams]: 'Teams',
      [SizeSubCategory.Startups]: 'Startups',
      [UseCaseSubCategory.DevSecOps]: 'DevSecOps',
      [UseCaseSubCategory.Automation]: 'Automation',
      [UseCaseSubCategory.DevOps]: 'DevOps',
    },
    readMore: 'Read customer story',
    aria: {
      // TODO: Update once known
      toggle: '',
    },
  },
  links: [
    {
      url: '/customer-stories',
      label: 'Explore customer stories',
    },
    {
      url: '/solutions',
      label: 'View all solutions',
    },
  ],
}
